import { apiErrorSchema } from '@libellus/shared';
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null;
  onUnauthorized: () => void;
  timeoutMs?: number;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
type Query = Record<string, string | number | boolean | undefined>;

interface RequestOptions<T> {
  body?: unknown;
  query?: Query;
  /** Validates the response at runtime so a contract drift fails loudly, not silently. */
  schema?: z.ZodType<T>;
  /** Lets the request finish after the page is closed (saving the reading position). */
  keepalive?: boolean;
  /** For slow requests, such as opening a book for the first time. */
  timeoutMs?: number;
}

type UploadOptions<T> = Pick<RequestOptions<T>, 'schema' | 'timeoutMs'>;

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  post<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  patch<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  put<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  delete(path: string): Promise<void>;
  /** Sends a file as the whole request body, typed by the blob. */
  upload<T>(path: string, file: Blob, options?: UploadOptions<T>): Promise<T>;
  /** Fetches a file the API generates (a PDF export). */
  download(path: string, options?: { query?: Query; timeoutMs?: number }): Promise<DownloadedFile>;
}

/** "attachment; filename*=UTF-8''pride.pdf" → "pride.pdf". */
function filenameFrom(disposition: string | null): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition ?? '')?.[1];
  if (encoded) return decodeURIComponent(encoded);
  return /filename="([^"]+)"/i.exec(disposition ?? '')?.[1] ?? 'download';
}

export function createApiClient({
  baseUrl,
  getToken,
  onUnauthorized,
  timeoutMs = 15_000,
}: ApiClientOptions): ApiClient {
  async function send(
    method: Method,
    path: string,
    options: Omit<RequestOptions<unknown>, 'schema'> & { accept: string; file?: Blob },
  ): Promise<Response> {
    const url = new URL(`${baseUrl}${path}`, window.location.origin);
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }

    const headers: Record<string, string> = { Accept: options.accept };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.file) headers['Content-Type'] = options.file.type;

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body:
          options.file ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
        signal: AbortSignal.timeout(options.timeoutMs ?? timeoutMs),
        keepalive: options.keepalive,
      });
    } catch (error) {
      const timedOut = error instanceof DOMException && error.name === 'TimeoutError';
      throw new ApiError(
        0,
        timedOut ? 'TIMEOUT' : 'NETWORK',
        timedOut ? 'The server took too long to answer.' : 'Cannot reach the server.',
      );
    }

    if (!response.ok) {
      if (response.status === 401 && token) onUnauthorized();
      const payload: unknown = await response.json().catch(() => null);
      const parsed = apiErrorSchema.safeParse(payload);
      throw parsed.success
        ? new ApiError(
            response.status,
            parsed.data.error.code,
            parsed.data.error.message,
            parsed.data.error.details,
          )
        : new ApiError(response.status, 'HTTP_ERROR', `Request failed (${response.status})`);
    }
    return response;
  }

  async function request<T>(
    method: Method,
    path: string,
    options: RequestOptions<T> & { file?: Blob } = {},
  ) {
    const response = await send(method, path, { ...options, accept: 'application/json' });
    if (response.status === 204) return undefined as T;
    const payload: unknown = await response.json().catch(() => null);
    return options.schema ? options.schema.parse(payload) : (payload as T);
  }

  return {
    get: (path, options) => request('GET', path, options),
    post: (path, options) => request('POST', path, options),
    patch: (path, options) => request('PATCH', path, options),
    put: (path, options) => request('PUT', path, options),
    delete: (path) => request<void>('DELETE', path),
    upload: (path, file, options) => request('POST', path, { ...options, file }),
    download: async (path, options = {}) => {
      const response = await send('GET', path, { ...options, accept: '*/*' });
      return {
        blob: await response.blob(),
        filename: filenameFrom(response.headers.get('Content-Disposition')),
      };
    },
  };
}
